import { Helmet } from "react-helmet-async";

const SITE = "https://bankdesks.com";
const SITE_NAME = "BankDesks.com";
const TWITTER_HANDLE = "@bankdesks";
const DEFAULT_IMAGE =
  "https://images.unsplash.com/photo-1768101225267-c6fc5678c113?auto=format&fit=crop&w=1200&h=630";

/**
 * Central SEO component — emits a production-grade meta-tag stack on every page.
 *
 * Props:
 *  - title          : page-specific title (H1 / pill text).
 *  - description    : < 160 char meta description.
 *  - path           : URL path for canonical & og:url.
 *  - image          : social-share image (og & twitter).
 *  - keywords       : comma-separated (or array) list of focus keywords.
 *  - jsonLd         : single schema.org object OR array of objects.
 *  - breadcrumbs    : [{name, path}] — rendered as BreadcrumbList.
 *  - robots         : override robots directive (default indexable).
 *  - type           : og:type (website | article | profile) — default `website`.
 *  - publishedTime  : ISO date for article:published_time.
 *  - modifiedTime   : ISO date for article:modified_time.
 *  - author         : author/publisher name.
 *  - noindex        : shortcut → robots=noindex,follow.
 *  - locale         : og:locale (default en_IN).
 *  - alternates     : [{hrefLang, path}] — emits hreflang tags.
 */
export const Seo = ({
  title,
  description,
  path = "/",
  image,
  keywords,
  jsonLd,
  breadcrumbs,
  robots,
  type = "website",
  publishedTime,
  modifiedTime,
  author = SITE_NAME,
  noindex = false,
  locale = "en_IN",
  alternates,
}) => {
  const url = `${SITE}${path}`;
  const fullTitle = title
    ? `${title} · ${SITE_NAME}`
    : `${SITE_NAME} · India's Banks & NBFCs Contact Directory — Nodal, Grievance, Recovery & Legal Heads`;
  const desc =
    description ||
    "Free public directory of every RBI-registered Bank & NBFC in India with Principal Nodal, Grievance, Recovery and Legal Head contacts. 9,381 institutions indexed.";
  const img = image || DEFAULT_IMAGE;
  const kw = Array.isArray(keywords) ? keywords.filter(Boolean).join(", ") : keywords;
  const robotsDirective =
    robots ||
    (noindex
      ? "noindex, follow"
      : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");

  const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet prioritizeSeoTags>
      {/* Primary */}
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {kw && <meta name="keywords" content={kw} />}
      <meta name="author" content={author} />
      <meta name="publisher" content={SITE_NAME} />
      <link rel="canonical" href={url} />

      {/* Feeds / sitemaps — helps Google Publisher Center + feed-reader discovery */}
      <link
        rel="alternate"
        type="application/rss+xml"
        title="BankDesks.com — Newly Enriched Banks & NBFCs"
        href={`${SITE}/api/feed.xml`}
      />
      <link
        rel="alternate"
        type="application/xml"
        title="BankDesks.com — Google News Sitemap"
        href={`${SITE}/api/sitemap-news.xml`}
      />

      {/* Robots */}
      <meta name="robots" content={robotsDirective} />
      <meta name="googlebot" content={robotsDirective} />
      <meta name="bingbot" content={robotsDirective} />

      {/* Geo targeting */}
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      <meta name="ICBM" content="20.5937, 78.9629" />
      <meta name="language" content="English" />
      <meta httpEquiv="content-language" content="en-IN" />

      {/* Alternate languages / regions — self-referencing canonical for Googlebot hreflang clustering */}
      <link rel="alternate" hrefLang="en-IN" href={url} />
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />
      {alternates?.map((a) => (
        <link
          key={a.hrefLang}
          rel="alternate"
          hrefLang={a.hrefLang}
          href={`${SITE}${a.path}`}
        />
      ))}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || SITE_NAME} />
      <meta property="og:locale" content={locale} />

      {/* Article meta (only relevant when type=article but harmless otherwise) */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && author && (
        <meta property="article:author" content={author} />
      )}
      {type === "article" && <meta property="article:section" content="Finance" />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      <meta name="twitter:image:alt" content={title || SITE_NAME} />

      {/* Mobile / App */}
      <meta name="theme-color" content="#002FA7" />
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
      <meta name="application-name" content={SITE_NAME} />
      <meta name="format-detection" content="telephone=no" />

      {/* Misc SEO helpers */}
      <meta name="rating" content="General" />
      <meta name="referrer" content="strict-origin-when-cross-origin" />
      <meta name="distribution" content="global" />
      <meta name="revisit-after" content="3 days" />

      {/* Structured data */}
      {jsonLdArray.map((obj, i) => (
        <script key={`jsonld-${i}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}

      {breadcrumbs && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: breadcrumbs.map((b, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: b.name,
              item: `${SITE}${b.path}`,
            })),
          })}
        </script>
      )}
    </Helmet>
  );
};

export const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const SITE_URL = SITE;
